import { useDebounce } from '@aimsos/hooks';
import { ChevronDown } from 'lucide-react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import UiAvatar from '../Avatar.jsx';
import Dropdown from '../Dropdown.jsx';
import SearchInput from '../SearchInput.jsx';
import Skeleton from '../Skeleton.jsx';
import {
  filterOptionsByQuery,
  resolveOptionAvatarClassName,
  resolveOptionLabelText,
} from './searchableSelect.utils.js';

/**
 * @typedef {object} SearchableSelectOption
 * @property {string} id
 * @property {string} name
 * @property {string} [subtitle]
 * @property {string} [initial] — texto del avatar (p. ej. dos letras); si no hay, se deduce de `name`
 * @property {string} [avatarSrc] — imagen opcional; si hay, se ignora el fallback por iniciales/gradiente
 * @property {string} [avatarClassName] — clases Tailwind del fallback (gradiente o color sólido)
 */

/** Avatar del design system: iniciales o `src`, borde invisible en fallback tipo “chip”. */
const OptionAvatar = memo(function SearchableSelectOptionAvatar({
  option,
  presetIndex,
  variant = 'row',
}) {
  const initials = resolveOptionLabelText(option);
  const hasImage = typeof option.avatarSrc === 'string' && option.avatarSrc.trim().length > 0;
  const bgClass = hasImage ? '' : resolveOptionAvatarClassName(option, presetIndex);

  const sizeClass =
    variant === 'trigger'
      ? '!h-7 !w-7 !min-h-[1.75rem] !min-w-[1.75rem] text-[11px] [&>span]:text-[11px]'
      : '';

  return (
    <UiAvatar
      src={hasImage ? option.avatarSrc : undefined}
      alt={option.name ?? 'Option'}
      initials={initials}
      size={variant === 'row' ? 'lg' : 'md'}
      className={[
        '!border-transparent',
        hasImage ? '' : bgClass,
        'text-white [&>span]:font-bold [&>span]:leading-none [&>span]:text-white',
        sizeClass,
      ]
        .filter(Boolean)
        .join(' ')}
    />
  );
});

const OptionRow = memo(function SearchableSelectOptionRow({
  option,
  presetIndex,
  isSelected,
  onSelect,
  showAvatar = true,
}) {
  const handleClick = useCallback(() => {
    onSelect(option);
  }, [onSelect, option]);

  return (
    <Dropdown.Item
      onClick={handleClick}
      className={[
        '!items-center !gap-3 !rounded-md !px-3 !py-2 !text-content',
        isSelected
          ? '!bg-brand-50 hover:!bg-brand-100 dark:!bg-brand-900/30 dark:hover:!bg-brand-900/50'
          : 'hover:!bg-surface-hover-subtle',
      ].join(' ')}
    >
      <div className="flex w-full min-w-0 items-center gap-3">
        {showAvatar ? <OptionAvatar option={option} presetIndex={presetIndex} variant="row" /> : null}
        <div className="min-w-0 flex-1 text-left">
          <p className="truncate text-sm font-medium text-content">{option.name}</p>
          {option.subtitle ? (
            <p className="truncate text-xs text-content-muted">{option.subtitle}</p>
          ) : null}
        </div>
      </div>
    </Dropdown.Item>
  );
});

/**
 * Selector genérico con disparador (avatar + etiqueta + chevron), búsqueda
 * y lista de opciones. Útil para agentes, workspaces, proyectos, miembros, etc.
 *
 * @param {(option: SearchableSelectOption) => string} [getOptionId] — por defecto `option => option.id`
 * @param {SearchableSelectOption} [selectedOption] — opción de respaldo para rotular el trigger cuando `value` no está en `options` (p. ej. selección fuera de la página cargada en modo servidor)
 * @param {(query: string) => void} [onChange] — callback con el texto de búsqueda tras `debounceMs` sin nuevas pulsaciones
 * @param {boolean} [filterLocally=true] — filtra `options` en memoria por `name`/`subtitle`. Pon `false` cuando la búsqueda la resuelve el servidor (vía `onChange`) y `options` ya llega filtrado
 * @param {() => void} [onOpen] — se llama cuando el menú se abre (p. ej. para refrescar/resetear opciones en modo servidor)
 * @param {() => void} [onLoadMore] — se llama al hacer scroll cerca del fondo de la lista (scroll infinito); úsalo junto a `hasMore`
 * @param {boolean} [hasMore=false] — hay más ítems por cargar; habilita el disparador de scroll y el loader inferior
 * @param {boolean} [isLoadingMore=false] — se está cargando la siguiente página; muestra un loader al final y pausa el disparador
 * @param {string} [loadingMoreLabel] — texto opcional junto al spinner del loader inferior
 * @param {number} [debounceMs=300] — retardo antes de aplicar filtro local y de notificar `onChange` (0 = sin debounce)
 * @param {boolean} [isLoading] — muestra placeholders `Skeleton` en el listado de opciones
 * @param {number} [loadingRows] — cantidad de filas skeleton cuando `isLoading` es true
 * @param {'shimmer'|'pulse'} [loadingAnimationStyle='shimmer'] — animación de los skeletons al cargar
 * @param {boolean} [showAvatar=true] — muestra/oculta avatar en trigger y en filas del menú
 * @param {string} [menuListMaxHeight='16rem'] — valor para `max-height` vía `--ss-select-list-max` + Tailwind (`none` / vacío = sin tope)
 * @param {(ctx: { selected: SearchableSelectOption | null, isOpen: boolean, disabled: boolean }) => React.ReactNode} [renderTrigger]
 */
const SearchableSelect = ({
  options = [],
  value: selectedId,
  onSelect,
  getOptionId = (o) => o.id,
  selectedOption,
  onChange,
  filterLocally = true,
  onOpen,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  loadingMoreLabel,
  debounceMs = 300,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  menuTitle,
  emptyMessage = 'No results',
  isLoading = false,
  loadingRows = 6,
  loadingAnimationStyle = 'shimmer',
  showAvatar = true,
  disabled = false,
  className = '',
  menuClassName = '',
  menuListMaxHeight = '16rem',
  renderTrigger,
  ariaLabel,
}) => {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const debouncedQuery = useDebounce(query, debounceMs);
  const lastEmittedQueryRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  const onChangeRef = useRef(onChange);
  onSelectRef.current = onSelect;
  onChangeRef.current = onChange;

  const handleSelectOption = useCallback((option) => {
    onSelectRef.current?.(option);
  }, []);

  const optionIndexById = useMemo(() => {
    const indexById = new Map();
    options.forEach((option, index) => {
      indexById.set(getOptionId(option), index);
    });
    return indexById;
  }, [options, getOptionId]);

  const selected = useMemo(() => {
    const found = options.find((o) => getOptionId(o) === selectedId);
    if (found) return found;
    // Fallback so the trigger can still show the current selection even when it
    // is not part of the loaded/filtered options (server-side search pages).
    if (selectedOption && getOptionId(selectedOption) === selectedId) {
      return selectedOption;
    }
    return null;
  }, [options, selectedId, getOptionId, selectedOption]);

  const selectedPresetIndex = useMemo(() => {
    if (!selected) return 0;
    const i = optionIndexById.get(getOptionId(selected));
    return Number.isInteger(i) && i >= 0 ? i : 0;
  }, [selected, optionIndexById, getOptionId]);

  const filtered = useMemo(
    () => (filterLocally ? filterOptionsByQuery(options, debouncedQuery) : options),
    [filterLocally, options, debouncedQuery],
  );

  useEffect(() => {
    if (lastEmittedQueryRef.current === null) {
      lastEmittedQueryRef.current = debouncedQuery;
      return;
    }
    if (lastEmittedQueryRef.current === debouncedQuery) return;
    lastEmittedQueryRef.current = debouncedQuery;
    onChangeRef.current?.(debouncedQuery);
  }, [debouncedQuery]);

  const onOpenRef = useRef(onOpen);
  onOpenRef.current = onOpen;
  const handleOpenChange = useCallback((open) => {
    setIsOpen(open);
    if (open) onOpenRef.current?.();
    else setQuery('');
  }, []);

  const handleSearchInputChange = useCallback((event) => {
    setQuery(event.target.value);
  }, []);

  const handleSearchInputClear = useCallback(() => {
    setQuery('');
  }, []);

  // Infinite scroll: fire `onLoadMore` when the list is scrolled near its
  // bottom. Read the callback through a ref so the scroll handler stays stable
  // and never holds a stale reference.
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;
  const handleListScroll = useCallback(
    (event) => {
      if (!hasMore || isLoadingMore) return;
      const el = event.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight <= 56) {
        onLoadMoreRef.current?.();
      }
    },
    [hasMore, isLoadingMore],
  );

  const defaultTrigger = useMemo(
    () => (
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={ariaLabel ?? (selected ? selected.name : placeholder)}
        className={[
          'group flex max-w-full items-center gap-2 text-sm font-medium text-content transition-colors',
          'hover:text-content focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 focus-visible:ring-offset-2',
          'dark:focus-visible:ring-offset-gray-900',
          disabled ? 'pointer-events-none opacity-50' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {selected ? (
          <>
            {showAvatar ? (
              <OptionAvatar option={selected} presetIndex={selectedPresetIndex} variant="trigger" />
            ) : null}
            <div className="flex min-w-0 items-center gap-1.5">
              <span className="min-w-0 truncate">{selected.name}</span>
              <ChevronDown
                className="size-3.5 shrink-0 text-content-muted group-hover:text-content-secondary"
                aria-hidden
              />
            </div>
          </>
        ) : (
          <>
            <span className="text-content-secondary">{placeholder}</span>
            <ChevronDown className="size-3.5 shrink-0 text-content-muted" aria-hidden />
          </>
        )}
      </button>
    ),
    [ariaLabel, disabled, isOpen, placeholder, selected, selectedPresetIndex, showAvatar],
  );

  const trigger = renderTrigger ? renderTrigger({ selected, isOpen, disabled }) : defaultTrigger;

  const showTitle = menuTitle != null && String(menuTitle).trim() !== '';

  const listHasMaxHeight =
    menuListMaxHeight != null &&
    menuListMaxHeight !== 'none' &&
    String(menuListMaxHeight).trim() !== '';

  const listScrollClassName = [
    'overflow-y-auto p-2 scrollbar-aimsos-auto-sm',
    listHasMaxHeight ? 'max-h-[var(--ss-select-list-max)]' : 'max-h-none',
  ].join(' ');

  const listScrollStyle = listHasMaxHeight
    ? { ['--ss-select-list-max']: menuListMaxHeight }
    : undefined;

  return (
    <Dropdown
      trigger={trigger}
      menuPosition="bottom"
      alignMenu="left"
      width="20rem"
      menuMaxHeight="none"
      usePortal
      disabled={disabled}
      onOpenChange={handleOpenChange}
      className={className}
      menuClassName={[
        'p-0 shadow-lg dark:shadow-[0px_4px_12px_rgba(0,0,0,0.45)]',
        menuClassName,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div
        className={[
          'border-b border-edge p-3',
          showTitle ? 'pb-3' : '',
        ].join(' ')}
      >
        {showTitle ? (
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-content-muted">
            {menuTitle}
          </p>
        ) : null}
        <SearchInput
          value={query}
          onChange={handleSearchInputChange}
          onClear={handleSearchInputClear}
          placeholder={searchPlaceholder}
          className="w-full"
          autoComplete="off"
          aria-label={searchPlaceholder}
        />
      </div>

      <div className={listScrollClassName} style={listScrollStyle} onScroll={handleListScroll}>
        {isLoading ? (
          <div className="space-y-2 px-1 py-1" aria-live="polite" aria-busy="true">
            {Array.from({ length: Math.max(1, loadingRows) }).map((_, index) => (
              <div key={`loading-row-${index}`} className="flex items-center gap-3 rounded-md px-2 py-1.5">
                <Skeleton
                  width={40}
                  height={40}
                  borderRadius="9999px"
                  animationStyle={loadingAnimationStyle}
                />
                <div className="flex-1 space-y-2">
                  <Skeleton
                    width="55%"
                    height={12}
                    borderRadius={6}
                    animationStyle={loadingAnimationStyle}
                  />
                  <Skeleton
                    width="80%"
                    height={10}
                    borderRadius={6}
                    animationStyle={loadingAnimationStyle}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <p className="px-2 py-6 text-center text-sm text-content-muted">{emptyMessage}</p>
        ) : (
          <div className="flex flex-col gap-1">
            {filtered.map((opt) => {
              const id = getOptionId(opt);
              const optionIndex = optionIndexById.get(id);
              const presetIndex =
                Number.isInteger(optionIndex) && optionIndex >= 0 ? optionIndex : 0;
              return (
                <OptionRow
                  key={id}
                  option={opt}
                  presetIndex={presetIndex}
                  isSelected={selectedId === id}
                  onSelect={handleSelectOption}
                  showAvatar={showAvatar}
                />
              );
            })}
          </div>
        )}
        {!isLoading && isLoadingMore ? (
          <div
            className="flex items-center justify-center gap-2 px-2 py-3 text-xs text-content-muted"
            aria-live="polite"
            aria-busy="true"
          >
            <span
              className="h-4 w-4 animate-spin rounded-full border-2 border-content-muted/40 border-t-content-muted"
              aria-hidden
            />
            {loadingMoreLabel ? <span>{loadingMoreLabel}</span> : null}
          </div>
        ) : null}
      </div>
    </Dropdown>
  );
};

export default SearchableSelect;
